import { ZERO_WEI } from '@kwenta/sdk/constants'
import { type FuturesMarketAsset, PositionSide } from '@kwenta/sdk/types'
import { formatPercent, getDisplayAsset } from '@kwenta/sdk/utils'
import { wei } from '@kwenta/wei'
import isEqual from 'lodash/isEqual'
import dynamic from 'next/dynamic'
import Router, { useRouter } from 'next/router'
import { type FC, memo, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { css } from 'styled-components'
import type { FuturesPosition, FuturesPositionWithPrice } from 'types/futures'

import UploadIcon from 'assets/svg/futures/upload-icon.svg'
import Currency from 'components/Currency'
import { DesktopOnlyView, MobileOrTabletView } from 'components/Media'
import Pill, { TransactionPill } from 'components/Pill'
import PnlTooltip, { ToolTipTrigger } from 'components/PnlTooltip'
import Spacer from 'components/Spacer'
import { TableNoResults } from 'components/Table'
import { Body, NumericValue } from 'components/Text'
import Tooltip from 'components/Tooltip/Tooltip'
import { FlexDiv, FlexDivCol, FlexDivRow, FlexDivRowCentered } from 'components/layout/flex'
import { NO_VALUE } from 'constants/placeholder'
import ROUTES from 'constants/routes'
import PositionType from 'sections/futures/PositionType'
import { setShowEditPositionModal } from 'state/app/reducer'
import { fetchTradesForPosition } from 'state/futures/actions'
import {
	selectMarkPrices,
	selectMarketAsset,
	selectPerpsProvider,
	selectUserInfoCollapsed,
	selectUserInfoShowAllMarkets,
} from 'state/futures/common/selectors'
import {
	selectAccountV3InitialMargin,
	selectActivePositions,
	selectIsIsolatedMarginProvider,
	selectShowFunding,
	selectShowRealizedPnL,
	selectTraderPositionHistoryQueryStatus,
} from 'state/futures/selectors'
import { selectCondOrdersEnabled } from 'state/futures/snxPerpsV3/selectors'
import { useAppDispatch, useAppSelector } from 'state/hooks'
import { FetchStatus } from 'state/types'
import media from 'styles/media'
import { providerIsCrossMargin, unserializePositionDetails } from 'utils/futures'

import { EmptyTableContainer } from '../../../components/Table/Table'
import TableFilters from '../MobileTrade/UserTabs/TableFilters'

import { createSelector } from '@reduxjs/toolkit'
import { useMarketPageRoute } from 'hooks/useMarketPageRoute'
import { selectOffchainPricesInfo } from 'state/prices/selectors'
import EditPositionButton from './EditPositionButton'
import TableMarketDetails, { WarningBadge } from './TableMarketDetails'

const ShareModal = dynamic(() => import('../ShareModal/ShareModal'), { ssr: false })

const selectPositions = createSelector(
	selectActivePositions,
	selectMarkPrices,
	selectOffchainPricesInfo,
	selectAccountV3InitialMargin,
	selectPerpsProvider,
	(positions, markPrices, pricesInfo, initialMargin, perpsProvider) => {
		return positions.map((p) => {
			const basePosition = {
				...p,
				marketPrice: markPrices[p.market.asset] || '0',
				priceInfo: pricesInfo[p.market.asset],
			}
			if (!providerIsCrossMargin(perpsProvider)) return basePosition

			const pnl = wei(p.details.pnl.uPnl?.pnl ?? '0')
			return {
				...basePosition,
				details: {
					...p.details,
					pnl: {
						...p.details.pnl,
						uPnl: {
							...p.details.pnl.uPnl,
							pnl: pnl.toString(),
							pnlPct: initialMargin
								? pnl
										.add(p.details.fees.accruedFunding ?? '0')
										.div(initialMargin)
										.toString()
								: '0',
						},
					},
				},
			}
		})
	}
)

type Props = {
	primaryBackground?: boolean
}
const PositionsTable: FC<Props> = memo(({ primaryBackground }: Props) => {
	const { t } = useTranslation()
	const router = useRouter()
	const dispatch = useAppDispatch()

	const currentMarket = useAppSelector(selectMarketAsset)
	const perpsProvider = useAppSelector(selectPerpsProvider)
	const userInfoCollapsed = useAppSelector(selectUserInfoCollapsed)
	const userInfoShowAllMarkets = useAppSelector(selectUserInfoShowAllMarkets)
	const showRealizedPnL = useAppSelector(selectShowRealizedPnL)
	const showFunding = useAppSelector(selectShowFunding)
	const condOrdersEnabled = useAppSelector(selectCondOrdersEnabled)
	const positions = useAppSelector(selectPositions, isEqual)
	const isIsolatedMargin = useAppSelector(selectIsIsolatedMarginProvider)
	const queryStatus = useAppSelector(selectTraderPositionHistoryQueryStatus, isEqual)

	const [showShareModal, setShowShareModal] = useState(false)
	const [sharePosition, setSharePosition] = useState<FuturesPositionWithPrice | null>(null)

	const isMarketsPage = useMemo(() => router.pathname.includes('/market'), [router.pathname])
	const marketPageRoute = useMarketPageRoute()

	const data = useMemo(() => {
		const formatted = positions
			.map((p) => ({
				...p,
				marketPrice: wei(p.marketPrice),
			}))
			.filter((p) => p.market.asset === currentMarket || userInfoShowAllMarkets || !isMarketsPage)
			.sort((a) => (a.market.asset === currentMarket ? -1 : 1))

		return formatted
	}, [positions, currentMarket, userInfoShowAllMarkets, isMarketsPage])

	const handleOpenShareModal = useCallback((share: FuturesPositionWithPrice) => {
		setSharePosition(share)
		setShowShareModal((s) => !s)
	}, [])

	const handleOpenPositionCloseModal = useCallback(
		(marketAsset: FuturesMarketAsset) => () => {
			dispatch(
				setShowEditPositionModal({
					type: isIsolatedMargin ? 'smart_margin_close_position' : 'cross_margin_close_position',
					marketAsset: marketAsset,
				})
			)
			if (!router.pathname.includes('market')) {
				router.push(marketPageRoute())
			}
		},
		[dispatch, router, isIsolatedMargin, marketPageRoute]
	)

	const getTooltipData = useCallback((position: FuturesPosition<string>) => {
		const positionDetails = unserializePositionDetails(position.details)
		return {
			pnl: positionDetails.pnl.rPnl.pnl,
			feesPaid: positionDetails.fees.feesPaid,
			funding: positionDetails.fees.accruedFunding,
			interestCharged: positionDetails.fees.owedInterest,
			totalPnl: positionDetails.pnl.totalPnl.netPnl.sub(positionDetails.pnl.uPnl?.pnl ?? ZERO_WEI),
			translationKey: 'position' as const,
		}
	}, [])

	return (
		<>
			<DesktopOnlyView>
				<TableContainer
					isSmartMargin={isIsolatedMargin}
					showRealizedPnL={showRealizedPnL}
					showFunding={showFunding}
					collapsed={userInfoCollapsed && isMarketsPage}
					empty={!data.length}
					primaryBackground={primaryBackground}
				>
					<HeadersRow className="header">
						<div>{t('futures.market.positions-table.market')}</div>
						<div>{t('futures.market.positions-table.side')}</div>
						<div>{t('futures.market.positions-table.size')}</div>
						<div>{t('futures.market.positions-table.avg-entry')}</div>
						<div>{t('futures.market.positions-table.market-price')}</div>
						<div>{t('futures.market.positions-table.liquidation-price')}</div>
						<div>{t('futures.market.positions-table.upnl')}</div>
						{showFunding && <div>{t('futures.market.positions-table.funding')}</div>}
						{showRealizedPnL && <div>{t('futures.market.positions-table.r-pnl')}</div>}
						{isIsolatedMargin && <div>{t('futures.market.positions-table.market-margin')}</div>}
						<div>{t('futures.market.positions-table.tpsl')}</div>
						<div>{t('futures.market.positions-table.actions')}</div>
					</HeadersRow>
					{!data.length && <EmptyTable />}
					{data.map((row) => (
						<PositionRowDesktop key={row.market.asset}>
							<PositionCell liquidated={row.details.liquidation.isLiquidated}>
								<MarketDetailsContainer
									onClick={() =>
										router.push(ROUTES.Markets.MarketPair(row.market.asset, perpsProvider))
									}
								>
									<SideIndicator side={row.details.side} />
									<TableMarketDetails
										badge={
											row.details.liquidation.isLiquidated ? (
												<WarningBadge>
													{t('futures.market.user.open-orders.badges.liquidated')}
												</WarningBadge>
											) : undefined
										}
										perpsProvider={perpsProvider}
										assetKey={row.market.asset}
										marketName={getDisplayAsset(row.market.asset) ?? ''}
									/>
								</MarketDetailsContainer>
							</PositionCell>
							<PositionCell liquidated={row.details.liquidation.isLiquidated}>
								{row.details.liquidation.isLiquidated ? (
									<PositionType side={row.details.side} />
								) : (
									<PositionType side={row.details.side} />
								)}
							</PositionCell>
							<PositionCell liquidated={row.details.liquidation.isLiquidated}>
								<FlexDivRowCentered columnGap="5px">
									<ColWithButton>
										<div>
											<FlexDivRowCentered justifyContent="flex-start" columnGap="5px">
												<Currency.Price
													price={row.details.size}
													currencyKey={row.market.asset}
													formatOptions={{ suggestDecimals: true }}
												/>
												<Body>{getDisplayAsset(row.market.asset) ?? ''}</Body>
												<EditPositionButton
													data-testid="position-table-edit-size-button"
													modalType={'futures_edit_position_size'}
													marketAsset={row.market.asset}
													perpsProvider={perpsProvider}
												/>
											</FlexDivRowCentered>
											<Currency.Price
												price={row.details.margin.notionalValue}
												formatOptions={{ truncateOver: 1e6, maxDecimals: 2 }}
												colorType="secondary"
											/>
										</div>
										<Spacer width={5} />
									</ColWithButton>
								</FlexDivRowCentered>
							</PositionCell>
							<PositionCell liquidated={row.details.liquidation.isLiquidated}>
								{!row.details.price.avgEntryPrice ? (
									<Body>{NO_VALUE}</Body>
								) : (
									<Currency.Price
										price={row.details.price.avgEntryPrice}
										formatOptions={{ suggestDecimals: true }}
									/>
								)}
							</PositionCell>
							<PositionCell>
								{row.priceInfo && (
									<Currency.Price
										price={row.marketPrice}
										formatOptions={{ suggestDecimals: true }}
									/>
								)}
							</PositionCell>
							<PositionCell>
								{row.details.price.liquidationPrice && (
									<Currency.Price
										price={row.details.price.liquidationPrice}
										formatOptions={{ suggestDecimals: true }}
									/>
								)}
							</PositionCell>
							<PositionCell liquidated={row.details.liquidation.isLiquidated}>
								<FlexDivRowCentered columnGap="5px">
									<PnlContainer>
										<Currency.Price price={row.details.pnl.uPnl?.pnl ?? ZERO_WEI} colored />
										{row.details.pnl.uPnl?.pnlPct && !wei(row.details.pnl.uPnl.pnlPct).eq(0) && (
											<NumericValue
												data-testid="pos-table-pnl-pct"
												value={row.details.pnl.uPnl.pnlPct}
												colored
											>
												{formatPercent(row.details.pnl.uPnl.pnlPct)}
											</NumericValue>
										)}
									</PnlContainer>
								</FlexDivRowCentered>
							</PositionCell>
							{showFunding && (
								<PositionCell>
									<Currency.Price
										price={row.details.fees.accruedFunding}
										formatOptions={{ suggestDecimals: true }}
										colored
									/>
								</PositionCell>
							)}
							{showRealizedPnL && (
								<PositionCell liquidated={row.details.liquidation.isLiquidated}>
									<Tooltip
										id={`position-pnl-${row.market.marketName}`}
										show={queryStatus?.status !== FetchStatus.Loading}
										portal={true}
										trigger={
											<ToolTipTrigger>
												{wei(row.details.pnl.totalPnl.netPnl)
													.sub(row.details.pnl.uPnl?.pnl ?? ZERO_WEI)
													.gt(0) && <Invisible>-</Invisible>}
												<FlexDivCol>
													{row.details.pnl.totalPnl.netPnl ? (
														<Currency.Price
															price={wei(row.details.pnl.totalPnl.netPnl).sub(
																row.details.pnl.uPnl?.pnl ?? ZERO_WEI
															)}
															colored
														/>
													) : (
														<Body>{NO_VALUE}</Body>
													)}
													{isIsolatedMargin && (
														<NumericValue value={row.details.pnl.rPnl.netPnlPct} colored>
															{formatPercent(row.details.pnl.rPnl.netPnlPct)}
														</NumericValue>
													)}
												</FlexDivCol>
											</ToolTipTrigger>
										}
										onTriggerHover={() =>
											dispatch(
												fetchTradesForPosition([
													{
														provider: perpsProvider,
														positionId: row.details.id,
														asset: row.market.asset,
													},
												])
											)
										}
										content={<PnlTooltip data={getTooltipData(row)} />}
									/>
								</PositionCell>
							)}
							{isIsolatedMargin && (
								<PositionCell liquidated={row.details.liquidation.isLiquidated}>
									<FlexDivRowCentered columnGap="5px">
										<ColWithButton>
											<div>
												<FlexDivRowCentered justifyContent="flex-start" columnGap="5px">
													<NumericValue value={row.details.margin.remainingMargin} prefix="$" />
													<EditPositionButton
														modalType={'futures_edit_position_margin'}
														marketAsset={row.market.asset}
														perpsProvider={perpsProvider}
													/>
												</FlexDivRowCentered>
												{isIsolatedMargin && (
													<NumericValue
														value={row.details.margin.leverage}
														color="secondary"
														suffix="x"
													/>
												)}
											</div>
											<Spacer width={5} />
										</ColWithButton>
									</FlexDivRowCentered>
								</PositionCell>
							)}
							<PositionCell>
								<FlexDivCol>
									<FlexDivRowCentered justifyContent="flex-start" columnGap="5px">
										{!row.details.takeProfit ? (
											<Body>{NO_VALUE}</Body>
										) : (
											<Currency.Price price={row.details.takeProfit.targetPrice} />
										)}

										<EditPositionButton
											disabled={!condOrdersEnabled}
											modalType={'futures_edit_stop_loss_take_profit'}
											marketAsset={row.market.asset}
											perpsProvider={perpsProvider}
											data-testid="position-table-sltp-button"
										/>
									</FlexDivRowCentered>
									{!row.details.stopLoss ? (
										<Body>{NO_VALUE}</Body>
									) : (
										<Currency.Price price={row.details.stopLoss.targetPrice} />
									)}
								</FlexDivCol>
							</PositionCell>

							<PositionCell>
								{!row.details.liquidation.isLiquidated ? (
									<FlexDivRowCentered>
										<TransactionPill
											onClick={handleOpenPositionCloseModal(row.market.asset)}
											size="medium"
										>
											Close
										</TransactionPill>
										<>
											<Spacer width={4} />
											<TransactionPill
												data-testid="position-table-share-button"
												onClick={() => handleOpenShareModal(row)}
												size="medium"
											>
												<ShareIconContainer>
													<UploadIcon width={10} />
												</ShareIconContainer>
											</TransactionPill>
										</>
									</FlexDivRowCentered>
								) : null}
							</PositionCell>
						</PositionRowDesktop>
					))}
				</TableContainer>
			</DesktopOnlyView>
			<MobileOrTabletView>
				<>
					{data.length === 0 ? (
						<EmptyTable />
					) : (
						<>
							<TableFilters />
							{data.map((row) => (
								<PositionItem key={row.market.asset}>
									<PositionMeta
										$side={row.details.side}
										onClick={() =>
											Router.push(ROUTES.Markets.MarketPair(row.market.asset, perpsProvider))
										}
									>
										<FlexDiv>
											<div className="position-side-bar" />
											<div>
												<Body>{getDisplayAsset(row.market.asset)}</Body>
												<Currency.Price
													price={row.marketPrice}
													formatOptions={{ suggestDecimals: true }}
												/>
											</div>
										</FlexDiv>
										<FlexDivRowCentered style={{ columnGap: '5px' }}>
											<Pill size="medium" onClick={handleOpenPositionCloseModal(row.market.asset)}>
												Close
											</Pill>
											<Pill size="medium" onClick={() => handleOpenShareModal(row)}>
												<FlexDivRowCentered>Share</FlexDivRowCentered>
											</Pill>
										</FlexDivRowCentered>
									</PositionMeta>
									<PositionRow>
										<div>
											<Body color="secondary">Size</Body>
											<div>
												<FlexDivRow justifyContent="start">
													<Currency.Price price={row.details.size} currencyKey={row.market.asset} />
													<>
														<Spacer width={5} />
														<EditPositionButton
															data-testid="position-table-edit-size-button"
															modalType={'futures_edit_position_size'}
															marketAsset={row.market.asset}
															perpsProvider={perpsProvider}
														/>
													</>
												</FlexDivRow>
												<Spacer width={5} />
												<Currency.Price
													price={row.details.margin.notionalValue}
													formatOptions={{ truncateOver: 1e6 }}
													colorType="secondary"
												/>
											</div>
										</div>
										<div>
											<Body color="secondary">Side</Body>
											<FlexDivRow>
												<PositionType side={row.details.side} />
											</FlexDivRow>
										</div>
										<div>
											<Body color="secondary">Avg. Entry</Body>
											{row.details.price.avgEntryPrice === undefined ? (
												<Body>{NO_VALUE}</Body>
											) : (
												<Currency.Price price={row.details.price.avgEntryPrice} />
											)}
										</div>
										{isIsolatedMargin && (
											<div>
												<Body color="secondary">Market Margin</Body>
												<FlexDivRow justifyContent="start">
													<Currency.Price
														price={row.details.margin.remainingMargin}
														formatOptions={{ suggestDecimals: false }}
													/>
													<Spacer width={10} />
													<NumericValue
														color="secondary"
														value={row.details.margin.leverage}
														suffix="x"
													/>
													<Spacer width={5} />
													<EditPositionButton
														modalType={'futures_edit_position_margin'}
														marketAsset={row.market.asset}
														perpsProvider={perpsProvider}
													/>
												</FlexDivRow>
											</div>
										)}

										<div>
											<Body color="secondary">Liquidation</Body>
											{row.details.price.liquidationPrice ? (
												<Currency.Price
													price={row.details.price.liquidationPrice}
													colorType="preview"
												/>
											) : (
												NO_VALUE
											)}
										</div>
										<div>
											<Body color="secondary">Unrealized PnL</Body>
											<Currency.Price price={row.details.pnl.uPnl?.pnl ?? ZERO_WEI} colored />
										</div>
										<div>
											<Body color="secondary">TP/SL</Body>
											<FlexDivRow justifyContent="start">
												{row.details.takeProfit === undefined ? (
													<Body>{NO_VALUE}</Body>
												) : (
													<Currency.Price price={row.details.takeProfit.targetPrice} />
												)}
												<Body>/</Body>
												{row.details.stopLoss === undefined ? (
													<Body color="secondary">{NO_VALUE}</Body>
												) : (
													<Currency.Price
														price={row.details.stopLoss.targetPrice}
														colorType="secondary"
													/>
												)}
												<>
													<Spacer width={5} />
													<EditPositionButton
														modalType={'futures_edit_stop_loss_take_profit'}
														marketAsset={row.market.asset}
														perpsProvider={perpsProvider}
													/>
												</>
											</FlexDivRow>
										</div>
										<div>
											<Body color="secondary">Funding</Body>
											<Currency.Price price={row.details.fees.accruedFunding} colored />
										</div>
										<div>
											<Body color="secondary">Interest</Body>
											<Currency.Price price={row.details.fees.owedInterest} colored />
										</div>
									</PositionRow>
								</PositionItem>
							))}
						</>
					)}
				</>
			</MobileOrTabletView>
			{showShareModal && (
				<ShareModal sharePosition={sharePosition!} setShowShareModal={setShowShareModal} />
			)}
		</>
	)
})

export default PositionsTable

const calculateColumns = (
	isSmartMargin?: boolean,
	showRealizedPnL?: boolean,
	showFunding?: boolean
) => {
	let columnCount = 9
	if (isSmartMargin) columnCount += 1
	if (showRealizedPnL) columnCount += 1
	if (showFunding) columnCount += 1
	return columnCount
}

const EmptyTable = () => {
	const { t } = useTranslation()
	return (
		<EmptyTableContainer>
			<TableNoResults>{t('dashboard.overview.futures-positions-table.no-result')}</TableNoResults>
		</EmptyTableContainer>
	)
}

const HeadersRow = styled.div`
	display: contents;
	& > div {
		display: flex;
		align-items: center;
		padding: 0 10px;
		height: 32px;
		color: ${(props) => props.theme.colors.selectedTheme.newTheme.text.secondary};
		border-bottom: ${(props) => props.theme.colors.selectedTheme.border};
	}
`

const TableContainer = styled.div<{
	isSmartMargin?: boolean
	showRealizedPnL?: boolean
	showFunding?: boolean
	collapsed: boolean
	empty?: boolean
	primaryBackground?: boolean
}>`
	overflow: auto;
	position: relative;
	border-top: ${(props) => props.theme.colors.selectedTheme.border};
	height: ${({ collapsed }) => (collapsed ? '0' : '100%')};
	display: grid;
	grid-template-columns: repeat(
		${({ isSmartMargin, showRealizedPnL, showFunding }) =>
			calculateColumns(isSmartMargin, showRealizedPnL, showFunding)},
		1fr
	);
	align-content: flex-start;

	background-color: ${(props) =>
		props.primaryBackground
			? props.theme.colors.selectedTheme.newTheme.containers.primary.background
			: 'none'};

	${({ empty }) =>
		empty &&
		css`
			grid-template-rows: auto 1fr;
			& > div {
				grid-column: 1 / -1;
			}
		`}
`

const PositionCell = styled.div<{ liquidated?: boolean }>`
	display: flex;
	align-items: center;
	height: 44px;
	padding: 0 10px;
	opacity: ${(props) => (props.liquidated ? 0.5 : 1)};
`

const PositionRowDesktop = styled.div`
	display: contents;
	& > ${PositionCell}:first-child {
		padding-left: 0;
	}
`

const PnlContainer = styled.div`
	display: flex;
	flex-direction: column;
`

const MarketDetailsContainer = styled.div`
	display: flex;
	height: 100%;
	flex-direction: row;
	cursor: pointer;
`

const ColWithButton = styled.div`
	display: flex;
	flex-direction: row;
	align-content: center;
	align-items: center;
	${media.lessThan('xxl')`
		display: block;
	`}
`

const SideIndicator = styled.div<{ side: PositionSide }>`
	height: 100%;
	width: 2px;
	margin-right: 8px;
	background-color: ${(props) =>
		props.theme.colors.selectedTheme.newTheme.text[
			props.side === PositionSide.LONG ? 'positive' : 'negative'
		]};
`

const PositionMeta = styled.div<{ $side: PositionSide }>`
	display: flex;
	justify-content: space-between;
	margin-bottom: 15px;

	.position-side-bar {
		height: 100%;
		width: 4px;
		margin-right: 8px;
		background-color: ${(props) =>
			props.theme.colors.selectedTheme.newTheme.text[
				props.$side === PositionSide.LONG ? 'positive' : 'negative'
			]};
	}
`

const PositionItem = styled.div`
	margin: 0 20px;
	padding: 20px 0;
	${media.greaterThan('md')`
		padding: 15px 0;
	`}

	${media.greaterThan('lg')`
	padding: 8px 0;
`}

	&:not(:last-of-type) {
		border-bottom: ${(props) => props.theme.colors.selectedTheme.border};
	}
`

const PositionRow = styled.div`
	min-height: 22px;
	grid-template-columns: 1fr 1fr;
	display: grid;
	grid-gap: 10px;

	${media.greaterThan('md')`
		grid-template-columns: 1fr 1fr 1fr;
	`}

	${media.greaterThan('lg')`
		grid-template-columns: 1fr 1fr 1fr 1fr;
	`}

	&:not(:last-of-type) {
		margin-bottom: 10px;
	}
`

const ShareIconContainer = styled(FlexDivRowCentered)`
	margin-top: -2px;
`

const Invisible = styled.span`
	color: transparent;
`
